import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "../components";
// `Column` tipi barrel'da dışa aktarılmıyor; doğrudan kaynağından alınır.
import type { Column } from "../components/DataTable";
import { LoadingBlock } from "../components/Spinner";
import {
  DateRangePicker,
  type RangeValue,
} from "../components/DateRangePicker";
import { useCallableAction } from "../hooks/useCallableAction";
import { n } from "../lib/format";
import { csvIndir, csvMetni, csvSayi, dosyaParcasi } from "../lib/csv";

/* ══════════════════════════════════════════════════════════════════════════
 * ÜRETİM RAPORU — hangi üründen kaç adet üretildi.
 *
 * SUNUCU SÖZLEŞMESİ — `functions/src/reports/productionReport.ts` ile BİREBİR.
 *
 * ★ "ÜRETİLEN" İLE "STOKTAN GÖNDERİLEN" AYRI SÜTUNLARDIR. Hızlı gönderim
 * siparişi stoktan çıkar; onu üretim saymak aynı ürünü iki kez sayardı (bir kez
 * stok üretiminde, bir kez o stoğun satışında). Ayrımı sunucu yapar; ekran
 * yalnızca gösterir.
 *
 * Para ve maliyet İÇERMEZ — üç rol de görür (atölye kendi üretimini görebilmeli).
 * ═══════════════════════════════════════════════════════════════════════ */

interface ProductionRow {
  key: string;
  productId: string;
  sku: string;
  title: string;
  orderQty: number;
  stockQty: number;
  producedQty: number;
  fromStockQty: number;
}

interface SshRow {
  title: string;
  quantity: number;
  label: string;
  note: string;
}

interface ProductionReportResponse {
  preset: string;
  presetLabel: string;
  rangeLabel: string;
  days: number;
  recordCount: number;
  truncated: boolean;
  limit: number;
  beforeCoverage: boolean;
  /** "2026-08-12" */
  coverageStart: string;
  basis: string;
  rows: ProductionRow[];
  totals: { orderQty: number; stockQty: number; producedQty: number; fromStockQty: number };
  producedRecordCount: number;
  fromStockRecordCount: number;
  ssh: SshRow[];
  noItemRecords: number;
  invalidItems: number;
  mixedStockQty: number;
}

/** Toplam satırının anahtarı — gerçek satır anahtarları `sku:`/`id:`/`ad:` ile başlar. */
const TOTAL_KEY = "__toplam";

/** Seçilen aralık oturumlar arası korunur. */
const STORAGE_KEY = "balder.productionReport.range";

function loadRange(): RangeValue {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const v = JSON.parse(raw) as RangeValue;
      if (v && typeof v.preset === "string") return v;
    }
  } catch {
    // Bozuk kayıt sayfanın açılmasını engellemez.
  }
  return { preset: "this_month" };
}

/** "2026-08-12" → "12.08.2026" */
function trTarih(iso: string): string {
  const [y, m, d] = iso.split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Türkçe büyük/küçük harfe duyarsız arama anahtarı. */
function ara(s: string): string {
  return s.toLocaleLowerCase("tr");
}

function StatCard({ label, value, hint }: { label: string; value: number; hint?: string }) {
  return (
    <div className="card card-pad">
      <dt className="truncate text-sm font-medium text-gray-500">{label}</dt>
      <dd className="mt-1 text-[26px] font-bold tracking-tight text-gray-950 tabular-nums">
        {n(value, " adet")}
      </dd>
      {hint ? <p className="mt-1 text-xs leading-snug text-gray-500">{hint}</p> : null}
    </div>
  );
}

export default function ProductionReport() {
  const [range, setRange] = useState<RangeValue>(loadRange);
  const [data, setData] = useState<ProductionReportResponse | null>(null);
  const [q, setQ] = useState("");

  const load = useCallableAction<
    { preset: string; from?: string; to?: string },
    ProductionReportResponse
  >("getProductionReport");

  /**
   * ★ GEÇ GELEN ESKİ YANIT YENİSİNİ EZMEZ. "Maksimum" yavaş, "Bugün" hızlıdır;
   * art arda seçilince yavaş olan sonradan döner ve ekranda yanlış aralığın
   * rakamları kalırdı. Yalnızca SON isteğin yanıtı ekrana yazılır.
   */
  const seq = useRef(0);
  const run = useCallback(
    (v: RangeValue) => {
      const id = ++seq.current;
      load
        .run({ preset: v.preset, from: v.from, to: v.to })
        .then((res) => { if (res && id === seq.current) setData(res); })
        .catch(() => undefined);
    },
    [load],
  );

  useEffect(() => {
    run(range);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(range));
    } catch {
      // Depolama kapalıysa sayfa yine çalışır.
    }
    // `run` her render'da yeniden üretilmesin diye bağımlılık yalnızca aralıktır.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range.preset, range.from, range.to]);

  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const s = ara(q.trim());
    if (!s) return all;
    return all.filter((r) => ara(r.title).includes(s) || ara(r.sku).includes(s));
  }, [data, q]);

  /** Arama açıkken alt toplam, ekranda görünen satırların toplamıdır. */
  const shown = useMemo(
    () =>
      rows.reduce(
        (t, r) => ({
          orderQty: t.orderQty + r.orderQty,
          stockQty: t.stockQty + r.stockQty,
          producedQty: t.producedQty + r.producedQty,
          fromStockQty: t.fromStockQty + r.fromStockQty,
        }),
        { orderQty: 0, stockQty: 0, producedQty: 0, fromStockQty: 0 },
      ),
    [rows],
  );

  const filtered = q.trim() !== "";

  /**
   * ★ TOPLAM, TABLONUN SON SATIRIDIR. `DataTable`'ın alt bilgisi tek hücreye
   * yayılır; rakamlar sütunlarıyla hizalı dursun diye toplam ayrı bir satır
   * olarak eklenir. Arama açıkken ekranda GÖRÜNEN satırların toplamıdır.
   */
  const tableRows = useMemo<ProductionRow[]>(
    () =>
      rows.length === 0
        ? []
        : [
            ...rows,
            {
              key: TOTAL_KEY,
              productId: "",
              sku: "",
              title: `Toplam (${n(rows.length)} ürün${filtered ? ", aramaya göre" : ""})`,
              ...shown,
            },
          ],
    [rows, shown, filtered],
  );

  const columns: Array<Column<ProductionRow>> = useMemo(() => {
    const adet = (r: ProductionRow, v: number) =>
      r.key === TOTAL_KEY ? <span className="font-semibold text-gray-900">{n(v)}</span> : v ? n(v) : "—";
    return [
      {
        key: "title",
        header: "Ürün",
        render: (r) =>
          r.key === TOTAL_KEY ? (
            <p className="font-semibold text-gray-900">{r.title}</p>
          ) : (
            <div className="min-w-0">
              <p className="font-medium text-gray-900">{r.title}</p>
              {r.sku ? <p className="text-xs text-gray-500 sm:hidden">{r.sku}</p> : null}
            </div>
          ),
      },
      {
        key: "sku",
        header: "Stok kodu",
        hideOnMobile: true,
        render: (r) => (r.key === TOTAL_KEY ? "" : r.sku || "—"),
      },
      {
        key: "order",
        header: "Sipariş için",
        numeric: true,
        hideOnMobile: true,
        render: (r) => adet(r, r.orderQty),
      },
      {
        key: "stock",
        header: "Stok için",
        numeric: true,
        hideOnMobile: true,
        render: (r) => adet(r, r.stockQty),
      },
      {
        key: "produced",
        header: "Üretilen",
        numeric: true,
        render: (r) => (
          <span className={r.producedQty || r.key === TOTAL_KEY ? "font-semibold text-gray-900" : "text-gray-400"}>
            {r.producedQty || r.key === TOTAL_KEY ? n(r.producedQty) : "—"}
          </span>
        ),
      },
      {
        key: "fromStock",
        header: "Stoktan gönderilen",
        numeric: true,
        // Uzun başlık dar ekranda ürün sütununu sıkıştırmasın diye sarılabilir.
        className: "whitespace-normal",
        render: (r) => adet(r, r.fromStockQty),
      },
    ];
  }, []);

  /** Dosya ekranda görünenin AYNISIDIR — ikinci bir sorgu yok. */
  const exportCsv = useCallback(() => {
    if (!data) return;
    const basliklar = [
      "Ürün", "Stok kodu", "Sipariş için üretilen", "Stok için üretilen",
      "Toplam üretilen", "Stoktan gönderilen (üretim değil)",
    ];
    const satirlar = rows.map((r) => [
      r.title, r.sku, csvSayi(r.orderQty), csvSayi(r.stockQty),
      csvSayi(r.producedQty), csvSayi(r.fromStockQty),
    ]);
    const arama = q.trim();
    satirlar.push([
      arama ? `TOPLAM (aramaya göre: ${arama})` : "TOPLAM", "", csvSayi(shown.orderQty), csvSayi(shown.stockQty),
      csvSayi(shown.producedQty), csvSayi(shown.fromStockQty),
    ]);
    csvIndir(
      `uretim-raporu-${dosyaParcasi(data.rangeLabel)}${arama ? "-filtreli" : ""}.csv`,
      csvMetni(basliklar, satirlar),
    );
  }, [data, rows, shown, q]);

  const busy = load.loading;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-gray-950 sm:text-[28px] sm:leading-9">
            Üretim Raporu
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Hangi üründen kaç adet üretildi — üretimin tamamlandığı tarihe göre.
          </p>
        </div>
        <div className="shrink-0">
          <DateRangePicker
            value={range}
            onChange={setRange}
            resolvedLabel={data?.rangeLabel ?? null}
            busy={busy}
          />
        </div>
      </header>

      {load.error ? (
        <div role="alert" className="rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {load.error}
        </div>
      ) : null}

      {!data ? (
        busy ? <LoadingBlock /> : null
      ) : (
        <>
          {/* ── Kapsam ve eksik uyarıları — rakam eksikse EKRAN YAZAR ─────── */}
          {data.beforeCoverage || data.truncated || data.noItemRecords > 0 || data.invalidItems > 0 ||
          data.mixedStockQty > 0 ? (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              <ul className="list-disc space-y-1 pl-5">
                {data.beforeCoverage ? (
                  <li>
                    Rapor {trTarih(data.coverageStart)} ve sonrasında tamamlanan üretimleri kapsar.
                    Daha eski kayıtlarda ürün ve tamamlanma tarihi bilgisi olmadığı için o dönem
                    bu rakamlara dâhil değildir.
                  </li>
                ) : null}
                {data.truncated ? (
                  <li>
                    Bu aralıkta en az {n(data.limit)} kayıt var; yalnızca ilk {n(data.limit)} kayıt
                    tarandı, rakamlar eksik olabilir. Aralığı daraltın.
                  </li>
                ) : null}
                {data.noItemRecords > 0 ? (
                  <li>{n(data.noItemRecords)} üretim kaydında ürün bilgisi yok; rapora giremedi.</li>
                ) : null}
                {data.mixedStockQty > 0 ? (
                  <li>
                    {n(data.mixedStockQty)} adet stoklu ürün, üretime giren siparişlerin içinde yer
                    aldığı için "Sipariş için üretilen"e sayıldı. Bunlar depodan verildiyse o kadar
                    adet fazla sayılmıştır; kayıttan ayırt edilemiyor.
                  </li>
                ) : null}
                {data.invalidItems > 0 ? (
                  <li>{n(data.invalidItems)} kalemin adedi okunamadı; sayılmadı.</li>
                ) : null}
              </ul>
            </div>
          ) : null}

          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Toplam üretilen"
              value={data.totals.producedQty}
              hint={`${n(data.producedRecordCount)} üretim kaydı · ${data.rangeLabel}`}
            />
            <StatCard label="Sipariş için üretilen" value={data.totals.orderQty} />
            <StatCard label="Stok için üretilen" value={data.totals.stockQty} />
            <StatCard
              label="Stoktan gönderilen"
              value={data.totals.fromStockQty}
              hint={`${n(data.fromStockRecordCount)} hızlı gönderim · üretim değil, toplama girmez`}
            />
          </dl>

          <section aria-labelledby="urun-baslik" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 id="urun-baslik" className="section-heading">
                Ürün bazında
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <label className="sr-only" htmlFor="uretim-ara">Ürün ya da stok kodu ara</label>
                <input
                  id="uretim-ara"
                  type="search"
                  className="input w-56"
                  placeholder="Ürün ya da stok kodu ara"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={exportCsv}
                  disabled={rows.length === 0}
                >
                  Excel'e aktar
                </button>
              </div>
            </div>

            <DataTable
              caption={`${data.rangeLabel} aralığında ürün bazında üretim adetleri`}
              rows={tableRows}
              columns={columns}
              rowKey={(r) => r.key}
              emptyTitle={filtered ? "Aramayla eşleşen ürün yok" : "Bu aralıkta tamamlanan üretim yok"}
              emptyDescription={filtered ? undefined : "Tarih aralığını genişletmeyi deneyin."}
              footer={
                <>
                  Üretilen = sipariş için + stok için. Kumaş renkleri ana stok kodunda toplanır;
                  takım ürünler (masa + sandalye) takım olarak sayılır. "Stoktan gönderilen" hızlı
                  gönderimle depodan çıkan adettir; atölyede üretilmediği için "Üretilen"e girmez.
                </>
              }
            />
          </section>

          {data.ssh.length > 0 ? (
            <section aria-labelledby="ssh-baslik" className="space-y-3">
              <h2 id="ssh-baslik" className="section-heading">
                Satış sonrası parça üretimi
              </h2>
              <p className="text-xs text-gray-500">
                Tam ürün değil, parça üretimidir; yukarıdaki adetlere dâhil değildir.
              </p>
              <ul className="divide-y divide-gray-200 rounded-lg bg-white ring-1 ring-gray-200">
                {data.ssh.map((s, i) => (
                  <li key={`${s.label}:${i}`} className="px-4 py-3 text-sm">
                    <p className="font-medium text-gray-900">
                      {s.title || "(ürün belirtilmemiş)"}
                      <span className="ml-2 font-normal text-gray-700">{n(s.quantity, " adet")}</span>
                      <span className="ml-2 font-normal text-gray-500">{s.label}</span>
                    </p>
                    {s.note ? <p className="mt-0.5 text-gray-600">{s.note}</p> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </>
      )}
    </div>
  );
}
